// /reports/top-customers — Top Customers (PAGES.md §3.50)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { Avatar, Badge, Select } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { TopCustomerRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = TopCustomerRow & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    { key: 'rank', header: '#', render: (_v, row) => <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-xs font-bold text-white tabular-nums">{row.rank}</span> },
    {
      key: 'name',
      header: 'Customer',
      render: (_v, row) => (
        <div className="flex items-center gap-3">
          <Avatar name={row.name} size="sm" />
          <div>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</p>
            {row.loyalty_tier ? <p className="text-xs text-neutral-500 dark:text-neutral-400">{row.loyalty_tier}</p> : null}
          </div>
        </div>
      ),
    },
    { key: 'visit_count', header: 'Visits', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.visit_count.toLocaleString('en-IN')}</span> },
    { key: 'total_spent', header: 'Total Spent', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.total_spent, currency)}</span> },
    { key: 'avg_order_value', header: 'Avg Order', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.avg_order_value, currency)}</span> },
    { key: 'loyalty_tier', header: 'Tier', render: (_v, row) => row.loyalty_tier ? <Badge variant="info" size="sm">{row.loyalty_tier}</Badge> : <span className="text-neutral-400">—</span> },
  ];
}

export function TopCustomersReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [limit, setLimit] = useState<10 | 25 | 50>(10);
  const filter = useReportFilter(timeRange, undefined);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'top-customers',
    filter,
    extraKey: [limit],
    queryFn: (f) => reportsApi.getTopCustomers(f, limit),
  });

  const rows: Row[] = useMemo(
    () => (data?.rows ?? []).map((r) => ({ ...r, id: r.customer_id })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `top-customers-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Rank', get: (r: Row) => r.rank },
          { header: 'Customer', get: (r: Row) => r.name },
          { header: 'Visits', get: (r: Row) => r.visit_count },
          { header: `Total Spent (${symbol})`, get: (r: Row) => (r.total_spent / subunit).toFixed(csvDecimals) },
          { header: `Avg Order (${symbol})`, get: (r: Row) => (r.avg_order_value / subunit).toFixed(csvDecimals) },
          { header: 'Tier', get: (r: Row) => r.loyalty_tier ?? '' },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Top Customers', `Top customer: ${rows[0]?.name ?? '—'} (${formatPaise(rows[0]?.total_spent ?? 0, currency)})`);
    }
  };

  return (
    <ReportPageShell
      title="Top Customers"
      subtitle="Highest spenders with visit frequency, average order value and loyalty tier."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Top Customers' }]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={
        <ReportFilterBar
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          showLocation={false}
          extraFilters={
            <div className="w-full sm:w-32">
              <Select
                options={[
                  { value: '10', label: 'Top 10' },
                  { value: '25', label: 'Top 25' },
                  { value: '50', label: 'Top 50' },
                ]}
                value={String(limit)}
                onChange={(v) => setLimit(Number(v) as 10 | 25 | 50)}
              />
            </div>
          }
        />
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={0} />
      ) : error ? (
        <ReportError />
      ) : (
        <ReportTable title={`Top ${limit} Customers`} columns={columns} data={rows} emptyTitle="No customer data" totalCount={rows.length} />
      )}
    </ReportPageShell>
  );
}
